import { DatePipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DateInput, dateInputToDateTime } from '../../types/date-input';
import { Refreshable } from '../../types/refreshable';
import { ExcelService } from '../../services/excel.service';
import { DateTime } from 'luxon';

/* global console, require, Intl */

@Component({
  selector: 'app-other',
  template: require('./timestamp.component.html'),
})
export class TimestampComponent implements OnInit, Refreshable {
  show_peak!: boolean;
  show_holiday!: boolean;
  show_hours!: boolean;
  local_and_utc!: boolean;
  error!: boolean;
  errorMessage = '';

  dateStart: DateInput = {
    year: new Date().getFullYear(),
    month: new Date().getMonth(),
    day: new Date().getDate(),
  };
  dateEnd: DateInput = {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate(),
  };

  formControlResolution = new FormControl('15min');
  formControlCountryCode = new FormControl('AT');
  formControlDateFormat = new FormControl('Local');
  formControlTimezone = new FormControl('Europe/Vienna');

  constructor(@Inject(ExcelService) private excelService: ExcelService) {
    this.setDefaults();
    this.formControlResolution.valueChanges.subscribe(() =>
      this.checkHoliday()
    );
    this.formControlTimezone.disable();
    this.formControlDateFormat.valueChanges.subscribe((val) => {
      if (val === 'Local & UTC') {
        this.formControlTimezone.enable();
      } else {
        this.formControlTimezone.disable();
      }
      this.updateTimezone();
    });
  }

  getTimezoneOffset(date: Date) {
    let timezone_offset_min = date.getTimezoneOffset();
    let offset_hrs = Math.abs(timezone_offset_min / 60).toFixed(0);
    let offset_min = Math.abs(timezone_offset_min % 60).toFixed(0);
    let timezone_standard;
    if (parseInt(offset_hrs) < 10) {
      offset_hrs = '0' + offset_hrs;
    }
    if (parseInt(offset_min) < 10) {
      offset_min = '0' + offset_min;
    }
    if (timezone_offset_min < 0) {
      timezone_standard = '+' + offset_hrs + offset_min;
    } else if (timezone_offset_min > 0) {
      timezone_standard = '-' + offset_hrs + offset_min;
    } else if (timezone_offset_min === 0) {
      timezone_standard = 'Z';
    }
    return timezone_standard;
  }

  ngOnInit() {}

  setDefaults() {
    this.show_peak = false;
    this.show_holiday = false;
    this.show_hours = false;
    this.error = false;
    this.local_and_utc = true;
  }

  restoreDefaults() {
    this.setDefaults();
  }

  generateTimeSeries() {
    let timezone = this.formControlTimezone.value;
    if (this.formControlDateFormat.value !== 'Local & UTC') {
      if (Intl.DateTimeFormat().resolvedOptions().timeZone !== undefined) {
        timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
    }
    const start_date = this.getStartDateForResolution(
      dateInputToDateTime(this.dateStart)
    );
    const end_date = this.getEndDateForResolution(
      dateInputToDateTime(this.dateEnd)
    );
    let timezone_offset_begin = this.getTimezoneOffset(start_date.toJSDate());
    let timezone_offset_end = this.getTimezoneOffset(end_date.toJSDate());
    let timezone_offset;
    if (this.formControlDateFormat.value === 'UTC') {
      timezone_offset = 'Z';
    }
    let begin =
      start_date.toFormat('yyyy-MM-dd') +
      'T00:00:00' +
      (timezone_offset === undefined ? timezone_offset_begin : timezone_offset);
    let end =
      end_date.toFormat('yyyy-MM-dd') +
      'T00:00:00' +
      (timezone_offset === undefined ? timezone_offset_end : timezone_offset);
    this.excelService
      .generateTimeSeries(
        begin,
        end,
        this.formControlResolution.value!,
        this.formControlCountryCode.value!,
        this.show_peak,
        this.show_hours,
        this.show_holiday,
        timezone!,
        this.formControlDateFormat.value!
      )
      .then(() => {
        this.error = false;
      })
      .catch((error) => this.defaultErrorHandler(error));
  }

  getStartDateForResolution(start_date: DateTime) {
    if (this.formControlResolution.value === '1MS') {
      start_date = start_date.startOf('month');
    } else if (this.formControlResolution.value === '1QS') {
      start_date = start_date.startOf('quarter');
    } else if (this.formControlResolution.value === '1YS') {
      start_date = start_date.startOf('year');
    }
    return start_date;
  }

  getEndDateForResolution(end_date: DateTime) {
    if (this.formControlResolution.value === '1MS') {
      return end_date.plus({ months: 1 }).startOf('month');
    } else if (this.formControlResolution.value === '1QS') {
      return end_date.plus({ quarter: 1 }).startOf('quarter');
    } else if (this.formControlResolution.value === '1YS') {
      return end_date.plus({ years: 1 }).startOf('year');
    } else {
      return end_date.plus({ days: 1 }).startOf('day');
    }
  }

  defaultErrorHandler(error: any) {
    this.error = true;
    this.errorMessage = error.message;
    console.error(error);
  }

  updateTimezone() {
    if (
      this.formControlDateFormat.value !== 'UTC' &&
      this.formControlTimezone.value === ''
    ) {
      this.formControlTimezone.setValue('Europe/Vienna');
    }
  }

  checkHoliday() {
    if (
      this.formControlResolution.value === '1YS' ||
      this.formControlResolution.value === '1QS' ||
      this.formControlResolution.value === '1MS' ||
      this.formControlResolution.value === '1D'
    ) {
      this.local_and_utc = false;
      if (this.formControlResolution.value !== '1D') {
        this.show_holiday = false;
      }
    } else {
      this.local_and_utc = true;
    }
  }

  refresh() {
    this.ngOnInit();
  }
}
